import './index.scss';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

export type ContextMenuItem = {
  key: string;
  className?: string;
  content: React.ReactNode;
  href?: string;
  target?: string;
  rel?: string;
  disabled?: boolean;
  onClick?: () => void;
};

export type ContextMenuSection = {
  className?: string;
  items: (ContextMenuItem | null)[];
};

export type ContextMenuProps = {
  x: number;
  y: number;
  className?: string;
  isClosing?: boolean;
  sections: ContextMenuSection[];
  onRequestClose?: () => void;
};

const VIEWPORT_MARGIN = 12;

export default function ContextMenu(props: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (props.isClosing) {
      return;
    }

    const updatePosition = () => {
      if (!menuRef.current) return;

      const isMobile = window.innerWidth <= 650;
      const horizontalOffset = isMobile ? -12 : -20;
      const verticalOffset = isMobile ? 10 : 12;
      const rect = menuRef.current.getBoundingClientRect();

      const viewportLeft = window.scrollX;
      const viewportRight = viewportLeft + window.innerWidth;
      const viewportTop = window.scrollY;
      const viewportBottom = viewportTop + window.innerHeight;
      const appRoot = document.getElementById('root');
      const appRootRect = appRoot?.getBoundingClientRect();
      const pageBottom = appRootRect
        ? appRootRect.bottom + window.scrollY
        : document.documentElement.scrollHeight;
      const availableBottom = Math.min(viewportBottom, pageBottom);

      let left = props.x + horizontalOffset;
      const minLeft = viewportLeft + VIEWPORT_MARGIN;
      const maxLeft = viewportRight - rect.width - VIEWPORT_MARGIN;
      const minTop = viewportTop + VIEWPORT_MARGIN;
      const maxTop = viewportBottom - rect.height - VIEWPORT_MARGIN;
      let top = props.y + verticalOffset;

      left = Math.min(Math.max(minLeft, left), Math.max(minLeft, maxLeft));

      if (top + rect.height > availableBottom - VIEWPORT_MARGIN) {
        top = props.y - rect.height - verticalOffset;
      }

      top = Math.min(Math.max(minTop, top), Math.max(minTop, maxTop));
      setPosition({ left, top });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [props.isClosing, props.x, props.y]);

  useEffect(() => {
    if (!props.onRequestClose) {
      return;
    }

    // Dismissing on click rather than mousedown, in the capture phase, so the
    // click that closes the menu can be stopped before it reaches whatever it
    // landed on. On mousedown the browser still delivers the click afterwards,
    // which re-triggers the control that opened the menu in the first place.
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        event.stopPropagation();
        props.onRequestClose?.();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onRequestClose?.();
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [props.onRequestClose]);

  return ReactDOM.createPortal(<div
    ref={menuRef}
    className={`context-menu ${props.className || ''} ${props.isClosing ? 'closing' : 'opening'}`}
    style={{
      left: position ? `${position.left}px` : `${props.x}px`,
      top: position ? `${position.top}px` : `${props.y}px`,
      visibility: position ? 'visible' : 'hidden'
    }}
    onClick={(event) => {
      event.stopPropagation();
    }}
  >
    {props.sections.map((section, sectionIndex) => {
      return <React.Fragment key={`section-${sectionIndex}`}>
        {sectionIndex !== 0 ? <div className='context-menu-divider' /> : null}

        <div className={section.className}>
          {section.items.map((item) => {
            if (!item) {
              return null;
            }

            if (item.href && !item.disabled) {
              return <a
                key={item.key}
                className={item.className}
                href={item.href}
                target={item.target || '_blank'}
                rel={item.rel || 'noreferrer'}
                onClick={() => {
                  item.onClick?.();
                }}
              >
                {item.content}
              </a>;
            }

            return <button
              key={item.key}
              type='button'
              className={item.className}
              disabled={item.disabled}
              onClick={() => {
                if (!item.disabled) {
                  item.onClick?.();
                }
              }}
            >
              {item.content}
            </button>;
          })}
        </div>
      </React.Fragment>;
    })}
  </div>, document.body);
}
